import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DoorOpenIcon, FileTextIcon, MapPinIcon, PackageIcon, PlusIcon } from 'lucide-react';
import {
  Button,
  CardHeader,
  EmptyState,
  ErrorNotice,
  FactList,
  Field,
  LoadingState,
  MessageThread,
  Modal,
  Panel,
  Pill,
  SectionedChecklist,
  SelectInput,
  TextArea,
  TextInput,
  VENUE_SECTIONS,
  VENUE_SETTLED,
  VENUE_STATUSES,
  sectionTitlesOf,
  type MentionCandidate,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  addWorkspaceTask,
  deleteWorkspaceTask,
  listWorkspaceMembers,
  listWorkspaceMessages,
  postWorkspaceMessage,
  readWorkspaceChecklists,
  readWorkspaceMaterials,
  readWorkspaceRoom,
  saveWorkspaceMaterials,
  saveWorkspaceRoom,
  updateWorkspaceTask,
  checklistScopeKey,
  type ChecklistKind,
  type ChecklistScope,
  type MaterialsPlan,
  type PlannedVenue,
  type PlanningTask,
  type TaskChanges,
} from '@/lib/workspace';

/**
 * What the client works from on their portal, from BCE's side.
 *
 * The same records the client sees — the thread, the two checklists, the
 * venue and rooms, the receiving plan and what they confirmed on arrival —
 * read and written here as BCE. Everything staff do shows on the client's
 * side attributed to BCE, and everything the client does shows here.
 *
 * Each piece lives where staff look for it on the event: the thread under
 * Messages, the venue on its own tab, the receiving plan under Shipments,
 * and the checklists under Checklist beside BCE's own.
 */

/** How often the thread re-reads. The client's portal streams; here polling is enough. */
const MESSAGE_POLL_MS = 10_000;

const TASK_STATUSES = [
  ['NOT_STARTED', 'Not started'],
  ['IN_PROGRESS', 'In progress'],
  ['SUBMITTED', 'Submitted'],
  ['AWAITING_APPROVAL', 'Awaiting approval'],
  ['CONFIRMED', 'Confirmed'],
  ['NOT_REQUIRED', 'Not required'],
  ['OVERDUE', 'Overdue'],
  ['COMPLETED', 'Completed'],
] as const;
const SETTLED_TASK_STATUSES: ReadonlySet<string> = new Set(['CONFIRMED', 'COMPLETED', 'NOT_REQUIRED']);

const RESPONSIBLE_PARTIES = [
  ['BCE', 'BCE'],
  ['BOOKING_CONTACT', 'Regional booking contact'],
  ['ENTERPRISE_ADMIN', 'Enterprise administrator'],
  ['VENUE', 'Venue or property'],
  ['AV_PROVIDER', 'The client’s AV provider'],
  ['CSR_BENEFICIARY', 'CSR beneficiary'],
  ['FACILITATOR', 'BCE facilitator'],
  ['SHIPPING_CONTACT', 'Shipping or receiving contact'],
] as const;

const NEW_SECTION = '__new__';

/** A client-made item may be renamed, moved or deleted; one of BCE's eighteen keeps its name and place. */
function isClientItem(task: PlanningTask): boolean {
  return task.responsibilityKey === null || task.responsibilityKey.startsWith('custom:');
}

function readableDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Messages ────────────────────────────────────────────────────────────

/** The thread the client sees on their portal, posted to as BCE. */
export function ClientMessagesThread({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const messages = useQuery({
    queryKey: ['workspace-messages', eventId],
    queryFn: ({ signal }) => listWorkspaceMessages(eventId, signal),
    refetchInterval: MESSAGE_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const members = useQuery({
    queryKey: ['workspace-members', eventId],
    queryFn: ({ signal }) => listWorkspaceMembers(eventId, signal),
  });
  const post = useMutation({
    mutationFn: (message: { content: Parameters<typeof postWorkspaceMessage>[1]['content']; parentId?: string }) =>
      postWorkspaceMessage(eventId, message),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-messages', eventId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (messages.isPending) return <LoadingState label="Loading the thread…" />;
  if (messages.isError) return <ErrorNotice title="Thread unavailable" message={errorMessage(messages.error)} />;

  const candidates: MentionCandidate[] = (members.data?.members ?? [])
    .filter((member) => member.isActive)
    .map((member) => ({
      id: member.id,
      label: [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email,
    }));

  return (
    <Panel className="overflow-hidden">
      <div className="p-5 pb-4">
        <CardHeader
          className="mb-0"
          title="Messages"
          description="The thread the client sees on their portal. What you write here reads as BCE there; tag one of their people and they are told."
        />
      </div>
      <MessageThread
        messages={messages.data}
        mentionCandidates={candidates}
        onPost={(message) => post.mutate(message)}
        isPosting={post.isPending}
        placeholder="Message the client's team. Type @ to tag someone"
        empty={{ title: 'No messages yet', message: 'Nothing from the client so far. Write to them here and it lands on their portal as BCE.' }}
      />
    </Panel>
  );
}

// ─── Checklists ──────────────────────────────────────────────────────────

interface TaskDialog {
  readonly list: ChecklistKind;
  readonly editing: PlanningTask | null;
  readonly lockedSection: string | null;
}

/** Which of the client's two lists to show. */
export const CLIENT_LIST = { plan: 'PLAN', venue: 'VENUE' } as const;
export type ClientList = (typeof CLIENT_LIST)[keyof typeof CLIENT_LIST];

/** One of the client's checklists, shared with them on their portal. */
export function ClientChecklists({
  scope,
  list,
}: {
  /** Whose list: an event's workspace, or a campaign's plan. */
  readonly scope: ChecklistScope;
  readonly list: ClientList;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const scopeKey = checklistScopeKey(scope);
  const checklists = useQuery({
    queryKey: ['workspace-checklists', scopeKey],
    queryFn: ({ signal }) => readWorkspaceChecklists(scope, signal),
  });
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['workspace-checklists', scopeKey] });
    // A campaign's stage follows its plan, so the header may move too.
    if (scope.kind === 'campaign') await queryClient.invalidateQueries({ queryKey: ['campaign', scope.id] });
  };
  const fail = (err: unknown): void => {
    toast.error(errorMessage(err));
  };

  const add = useMutation({ mutationFn: (task: Parameters<typeof addWorkspaceTask>[1]) => addWorkspaceTask(scope, task), onSuccess: refresh, onError: fail });
  const update = useMutation({
    mutationFn: ({ taskId, changes }: { taskId: string; changes: TaskChanges }) => updateWorkspaceTask(scope, taskId, changes),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({ mutationFn: (taskId: string) => deleteWorkspaceTask(scope, taskId), onSuccess: refresh, onError: fail });
  const busy = add.isPending || update.isPending || remove.isPending;

  const [dialog, setDialog] = useState<TaskDialog | null>(null);
  const [title, setTitle] = useState('');
  const [sectionChoice, setSectionChoice] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [party, setParty] = useState('');
  const [name, setName] = useState('');
  const [due, setDue] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  function openAdd(list: ChecklistKind, lockedSection: string | null, asNew = false): void {
    setDialog({ list, editing: null, lockedSection });
    setTitle('');
    setSectionChoice(asNew ? NEW_SECTION : '');
    setNewSectionName('');
    setParty('');
    setName('');
    setDue('');
    setNotes('');
    setConfirmingDelete(false);
  }
  function openEdit(list: ChecklistKind, itemId: string): void {
    const source = list === 'VENUE' ? (checklists.data?.responsibilities ?? []) : (checklists.data?.tasks ?? []);
    const task = source.find((candidate) => candidate.id === itemId);
    if (!task) return;
    setDialog({ list, editing: task, lockedSection: null });
    setTitle(task.title);
    setSectionChoice(task.section ?? '');
    setNewSectionName('');
    setParty(task.responsibleParty ?? '');
    setName(task.responsibleName ?? '');
    setDue(task.dueAt ? task.dueAt.slice(0, 10) : '');
    setNotes(task.notes ?? '');
    setConfirmingDelete(false);
  }
  function submit(): void {
    if (!dialog || title.trim().length === 0) return;
    if (sectionChoice === NEW_SECTION && newSectionName.trim().length === 0) return;
    const section = dialog.lockedSection ?? (sectionChoice === NEW_SECTION ? newSectionName.trim() : sectionChoice);
    if (dialog.editing) {
      update.mutate({
        taskId: dialog.editing.id,
        changes: {
          ...(isClientItem(dialog.editing) ? { title: title.trim(), section: section || null } : {}),
          responsibleParty: party || null,
          responsibleName: name.trim(),
          dueAt: due ? new Date(`${due}T00:00:00`).toISOString() : null,
          notes: notes.trim(),
        },
      });
    } else {
      add.mutate({
        list: dialog.list,
        title: title.trim(),
        ...(section ? { section } : {}),
        ...(party ? { responsibleParty: party } : {}),
        ...(name.trim() ? { responsibleName: name.trim() } : {}),
        ...(due ? { dueAt: new Date(`${due}T00:00:00`).toISOString() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
    }
    setDialog(null);
  }

  if (checklists.isPending) return <LoadingState label="Loading the checklists…" />;
  if (checklists.isError) return <ErrorNotice title="Checklists unavailable" message={errorMessage(checklists.error)} />;
  const setStatus = (taskId: string, status: string): void => update.mutate({ taskId, changes: { status } });
  const editing = dialog?.editing ?? null;
  const fixedItem = editing !== null && !isClientItem(editing);

  return (
    <div className="space-y-5">
      {list === CLIENT_LIST.plan ? (
      <Panel className="overflow-hidden">
        <div className="p-5 pb-4">
          <CardHeader
            className="mb-0"
            title="Planning checklist"
            description={
              scope.kind === 'campaign'
                ? "What the client and BCE are lining up across the campaign, in the client's sections, with who owns each task. Once everything is settled the campaign moves to delivery."
                : "What the client and BCE are lining up before the day, in the client's sections, with who owns each task."
            }
            badge={<Pill tone="info">Shared with the client</Pill>}
            aside={
              <span className="flex items-center gap-2">
                <Button variant="secondary" size="compact" onClick={() => openAdd('PLAN', null, true)} disabled={busy}>
                  Add section
                </Button>
                <Button size="compact" onClick={() => openAdd('PLAN', null)} disabled={busy}>
                  Add task
                </Button>
              </span>
            }
          />
        </div>
        <SectionedChecklist
          items={checklists.data.tasks}
          statuses={TASK_STATUSES}
          doneStatus="COMPLETED"
          undoneStatus="NOT_STARTED"
          settledStatuses={SETTLED_TASK_STATUSES}
          fallbackSectionTitle="General"
          onSetStatus={setStatus}
          onAddItem={(section) => openAdd('PLAN', section)}
          onAddSection={() => openAdd('PLAN', null, true)}
          onEdit={(item) => openEdit('PLAN', item.id)}
          isSaving={busy}
          emptyTitle="Nothing on the plan yet"
          emptyMessage="Start a section such as Before the day, On the day or Afterwards, and add what needs lining up under it."
        />
      </Panel>
      ) : null}

      {list === CLIENT_LIST.venue ? (
      <Panel className="overflow-hidden">
        <div className="p-5 pb-4">
          <CardHeader
            className="mb-0"
            title="Venue checklist"
            description="The eighteen things BCE needs in place on the day, grouped by who the client would ask, plus anything they added."
            badge={<Pill tone="info">Shared with the client</Pill>}
            aside={
              <span className="flex items-center gap-2">
                <Button variant="secondary" size="compact" onClick={() => openAdd('VENUE', null, true)} disabled={busy}>
                  Add section
                </Button>
                <Button size="compact" onClick={() => openAdd('VENUE', null)} disabled={busy}>
                  Add item
                </Button>
              </span>
            }
          />
        </div>
        <SectionedChecklist
          items={checklists.data.responsibilities}
          fixedSections={VENUE_SECTIONS}
          statuses={VENUE_STATUSES}
          doneStatus="CONFIRMED"
          undoneStatus="NOT_STARTED"
          settledStatuses={VENUE_SETTLED}
          fallbackSectionTitle="Anything else"
          onSetStatus={setStatus}
          onAddItem={(section) => openAdd('VENUE', section)}
          onAddSection={() => openAdd('VENUE', null, true)}
          onEdit={(item) => openEdit('VENUE', item.id)}
          isSaving={busy}
          emptyTitle="The list is loading"
          emptyMessage="The eighteen items BCE needs in place appear here in a moment."
        />
      </Panel>
      ) : null}

      <Modal
        open={dialog !== null}
        title={
          editing
            ? fixedItem
              ? editing.title
              : 'Edit item'
            : dialog?.lockedSection
              ? `Add to ${dialog.lockedSection}`
              : sectionChoice === NEW_SECTION
                ? 'Add a section'
                : dialog?.list === 'VENUE'
                  ? 'Add a venue item'
                  : 'Add a task'
        }
        subtitle={
          fixedItem
            ? 'One of the eighteen BCE needs on every event, so it keeps its name and place. Everything else about it can change.'
            : dialog?.list === 'VENUE'
              ? 'Something else the venue or the client needs in place on the day.'
              : 'Something the client and BCE need to line up before the day.'
        }
        onClose={() => setDialog(null)}
        actions={
          <>
            {editing && !fixedItem ? (
              <Button
                variant="secondary"
                className="mr-auto text-rose-700"
                disabled={busy}
                onClick={() => {
                  if (!confirmingDelete) {
                    setConfirmingDelete(true);
                    return;
                  }
                  remove.mutate(editing.id);
                  setDialog(null);
                }}
              >
                {confirmingDelete ? 'Yes, delete it' : 'Delete'}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy || title.trim().length === 0 || (sectionChoice === NEW_SECTION && newSectionName.trim().length === 0)}>
              {editing ? 'Save' : dialog?.list === 'VENUE' ? 'Add item' : 'Add task'}
            </Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); submit(); }}>
          <Field label="What needs to happen" htmlFor="ws-task-title">
            <TextInput id="ws-task-title" readOnly={fixedItem} value={title} onChange={(event) => setTitle(event.target.value)} autoFocus placeholder={dialog?.list === 'VENUE' ? 'Confirm the loading dock hours' : 'Send the final headcount'} />
          </Field>
          {dialog && dialog.lockedSection === null && !fixedItem ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Section" htmlFor="ws-task-section">
                <SelectInput id="ws-task-section" value={sectionChoice} onChange={(event) => setSectionChoice(event.target.value)}>
                  <option value="">{dialog.list === 'VENUE' ? 'Anything else' : 'General'}</option>
                  {sectionTitlesOf(
                    dialog.list === 'VENUE' ? checklists.data.responsibilities : checklists.data.tasks,
                    dialog.list === 'VENUE' ? VENUE_SECTIONS : [],
                  ).map((sectionTitle) => (
                    <option key={sectionTitle} value={sectionTitle}>{sectionTitle}</option>
                  ))}
                  <option value={NEW_SECTION}>New section…</option>
                </SelectInput>
              </Field>
              {sectionChoice === NEW_SECTION ? (
                <Field label="Section name" htmlFor="ws-task-new-section">
                  <TextInput id="ws-task-new-section" value={newSectionName} onChange={(event) => setNewSectionName(event.target.value)} placeholder={dialog.list === 'VENUE' ? 'Catering' : 'Before the day'} />
                </Field>
              ) : null}
            </div>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Who is responsible" htmlFor="ws-task-party">
              <SelectInput id="ws-task-party" value={party} onChange={(event) => setParty(event.target.value)}>
                <option value="">Not decided yet</option>
                {RESPONSIBLE_PARTIES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Their name" htmlFor="ws-task-name" hint="Optional">
              <TextInput id="ws-task-name" value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field label="Due" htmlFor="ws-task-due" hint="Optional">
              <TextInput id="ws-task-due" type="date" value={due} onChange={(event) => setDue(event.target.value)} />
            </Field>
          </div>
          <Field label="Notes" htmlFor="ws-task-notes" hint="Optional">
            <TextArea id="ws-task-notes" rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>
        </form>
      </Modal>
    </div>
  );
}

// ─── Venue & rooms ───────────────────────────────────────────────────────

type RoomDialog =
  | { readonly kind: 'venue'; readonly index: number | null }
  | { readonly kind: 'room'; readonly venue: number; readonly index: number | null };

/** The venues and rooms the client's plan holds, managed from the event. */
export function VenueRoomsTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const room = useQuery({ queryKey: ['workspace-room', eventId], queryFn: ({ signal }) => readWorkspaceRoom(eventId, signal) });
  const save = useMutation({
    mutationFn: (venues: readonly PlannedVenue[]) => saveWorkspaceRoom(eventId, { venues }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-room', eventId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const [dialog, setDialog] = useState<RoomDialog | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const set = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft((previous) => ({ ...previous, [field]: event.target.value }));

  if (room.isPending) return <LoadingState label="Loading the venue…" />;
  if (room.isError) return <ErrorNotice title="Venue unavailable" message={errorMessage(room.error)} />;
  const plan = room.data.roomPlan;
  const venues = plan?.venues ?? [];

  function openVenue(index: number | null): void {
    const venue = index === null ? null : (venues[index] ?? null);
    setDraft({ name: venue?.name ?? '', address: venue?.address ?? '', contactName: venue?.contactName ?? '', contactPhone: venue?.contactPhone ?? '' });
    setDialog({ kind: 'venue', index });
  }
  function openRoom(venue: number, index: number | null): void {
    const entry = index === null ? null : (venues[venue]?.rooms[index] ?? null);
    setDraft({ name: entry?.name ?? '', dimensions: entry?.dimensions ?? '', purpose: entry?.purpose ?? '' });
    setDialog({ kind: 'room', venue, index });
  }
  function submit(): void {
    if (!dialog || (draft['name'] ?? '').trim().length === 0) return;
    const next: PlannedVenue[] = venues.map((venue) => ({ ...venue }));
    if (dialog.kind === 'venue') {
      const value: PlannedVenue = {
        name: (draft['name'] ?? '').trim(),
        ...(draft['address']?.trim() ? { address: draft['address'].trim() } : {}),
        ...(draft['contactName']?.trim() ? { contactName: draft['contactName'].trim() } : {}),
        ...(draft['contactPhone']?.trim() ? { contactPhone: draft['contactPhone'].trim() } : {}),
        rooms: dialog.index === null ? [] : (next[dialog.index]?.rooms ?? []),
      };
      if (dialog.index === null) next.push(value);
      else next[dialog.index] = value;
    } else {
      const target = next[dialog.venue];
      if (!target) return;
      const existing = dialog.index === null ? undefined : target.rooms[dialog.index];
      const value = {
        name: (draft['name'] ?? '').trim(),
        ...(draft['dimensions']?.trim() ? { dimensions: draft['dimensions'].trim() } : {}),
        ...(draft['purpose']?.trim() ? { purpose: draft['purpose'].trim() } : {}),
        ...(existing?.diagramUrl ? { diagramUrl: existing.diagramUrl } : {}),
      };
      const rooms =
        dialog.index === null
          ? [...target.rooms, value]
          : target.rooms.map((entry, i) => (i === dialog.index ? value : entry));
      next[dialog.venue] = { ...target, rooms };
    }
    save.mutate(next);
    setDialog(null);
  }
  function removeRoom(venue: number, index: number): void {
    const next = venues.map((entry, v) => (v === venue ? { ...entry, rooms: entry.rooms.filter((_, i) => i !== index) } : entry));
    save.mutate(next);
  }

  return (
    <div className="mt-6 space-y-5">
      {venues.length === 0 ? (
        <Panel className="p-5">
          <CardHeader title="Venue and rooms" description="Where the event happens, who BCE calls there, and the rooms in play." />
          <EmptyState
            icon={<MapPinIcon aria-hidden />}
            title="No venue on the workspace yet"
            message="The client usually adds it from their portal; add it here when they have told you by phone."
            action={<Button onClick={() => openVenue(null)} disabled={save.isPending}>Add venue</Button>}
          />
        </Panel>
      ) : (
        venues.map((venue, venueIndex) => (
          <Panel key={`${venue.name}-${venueIndex}`} className="overflow-hidden">
            <div className="p-5 pb-4">
              <CardHeader
                className="mb-0"
                title={venue.name}
                description={[venue.address, [venue.contactName, venue.contactPhone].filter(Boolean).join(' · ')].filter(Boolean).join(' · ') || 'No address or contact yet.'}
                aside={
                  <span className="flex items-center gap-2">
                    <Button variant="secondary" size="compact" onClick={() => openVenue(venueIndex)} disabled={save.isPending}>Edit venue</Button>
                    <Button size="compact" variant={venue.rooms.length === 0 ? 'primary' : 'secondary'} onClick={() => openRoom(venueIndex, null)} disabled={save.isPending}>Add room</Button>
                  </span>
                }
              />
            </div>
            {venue.rooms.length === 0 ? (
              <div className="border-t border-portal-line px-5 py-5">
                <EmptyState icon={<DoorOpenIcon aria-hidden />} title="No rooms yet" message="Add the rooms in use at this venue." />
              </div>
            ) : (
              venue.rooms.map((entry, index) => (
                <section key={`${entry.name}-${index}`} aria-label={entry.name} className="border-t border-portal-line">
                  <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                    <DoorOpenIcon aria-hidden className="size-5 shrink-0 text-portal-muted" />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <h3 className="text-portal-small font-bold">{entry.name}</h3>
                        {venueIndex === 0 && index === 0 ? <Pill tone="brand">Main room</Pill> : null}
                      </span>
                      {entry.dimensions || entry.purpose ? (
                        <span className="block text-portal-micro text-portal-muted">{[entry.dimensions, entry.purpose].filter(Boolean).join(' · ')}</span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-2">
                      {entry.diagramUrl ? (
                        <Button href={entry.diagramUrl} target="_blank" rel="noreferrer" variant="secondary" size="compact">
                          <FileTextIcon aria-hidden className="size-4" /> Floor plan
                        </Button>
                      ) : null}
                      <Button variant="secondary" size="compact" onClick={() => openRoom(venueIndex, index)} disabled={save.isPending}>Edit</Button>
                      <Button variant="secondary" size="compact" onClick={() => removeRoom(venueIndex, index)} disabled={save.isPending}>Remove</Button>
                    </span>
                  </div>
                </section>
              ))
            )}
          </Panel>
        ))
      )}
      {venues.length > 0 ? (
        <div className="flex justify-end">
          <Button variant="secondary" size="compact" onClick={() => openVenue(null)} disabled={save.isPending}>
            <PlusIcon aria-hidden className="size-4" /> Add another venue
          </Button>
        </div>
      ) : null}

      {plan ? (
        <Panel className="p-5">
          <CardHeader title="Room setup" description="What the client told BCE about the room, in their words." />
          <FactList
            columns={3}
            showEmpty
            facts={[
              { label: 'Teams', value: plan.teamCount ?? null },
              { label: 'Team size', value: plan.teamSize ?? null },
              { label: 'Tables', value: plan.tablesRequired ?? null },
              { label: 'Chairs', value: plan.chairsRequired ?? null },
              { label: 'Facilitator position', value: plan.facilitatorPosition },
              { label: 'Materials distribution', value: plan.materialsDistributionArea },
              { label: 'Donation staging', value: plan.donationStagingArea },
              { label: 'Accessibility', value: plan.accessibilityNotes },
              { label: 'Setup and breakdown', value: plan.setupBreakdownNotes },
            ]}
          />
          {plan.updatedByName ? <p className="mt-3 text-portal-micro text-portal-muted">Last updated by {plan.updatedByName}.</p> : null}
        </Panel>
      ) : null}

      <Modal
        open={dialog !== null}
        title={dialog?.kind === 'venue' ? (dialog.index === null ? 'Add a venue' : 'Edit venue') : dialog?.index === null ? 'Add a room' : 'Edit room'}
        subtitle={dialog?.kind === 'venue' ? 'Where the event happens and who BCE calls there. The client sees this on their portal.' : 'A room in use at this venue, with its size if known.'}
        onClose={() => setDialog(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submit} disabled={save.isPending || (draft['name'] ?? '').trim().length === 0}>Save</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); submit(); }}>
          {dialog?.kind === 'venue' ? (
            <>
              <Field label="Venue" htmlFor="ws-venue-name">
                <TextInput id="ws-venue-name" value={draft['name'] ?? ''} onChange={set('name')} autoFocus placeholder="Hyatt Regency Dallas" />
              </Field>
              <Field label="Address" htmlFor="ws-venue-address" hint="Optional">
                <TextInput id="ws-venue-address" value={draft['address'] ?? ''} onChange={set('address')} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Contact" htmlFor="ws-venue-contact" hint="Optional">
                  <TextInput id="ws-venue-contact" value={draft['contactName'] ?? ''} onChange={set('contactName')} />
                </Field>
                <Field label="Phone" htmlFor="ws-venue-phone" hint="Optional">
                  <TextInput id="ws-venue-phone" value={draft['contactPhone'] ?? ''} onChange={set('contactPhone')} />
                </Field>
              </div>
            </>
          ) : (
            <>
              <Field label="Room" htmlFor="ws-room-name">
                <TextInput id="ws-room-name" value={draft['name'] ?? ''} onChange={set('name')} autoFocus placeholder="Reunion Ballroom B" />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Dimensions" htmlFor="ws-room-dimensions" hint="Optional">
                  <TextInput id="ws-room-dimensions" value={draft['dimensions'] ?? ''} onChange={set('dimensions')} placeholder="80 × 120 ft" />
                </Field>
                <Field label="Used for" htmlFor="ws-room-purpose" hint="Optional">
                  <TextInput id="ws-room-purpose" value={draft['purpose'] ?? ''} onChange={set('purpose')} placeholder="Main activity" />
                </Field>
              </div>
            </>
          )}
        </form>
      </Modal>
    </div>
  );
}

// ─── Materials & freight ────────────────────────────────────────────────

const MATERIALS_TEXT_FIELDS: readonly { readonly key: keyof MaterialsPlan; readonly label: string; readonly long?: boolean }[] = [
  { key: 'receivingContactName', label: 'Receiving contact' },
  { key: 'receivingContactPhone', label: 'Receiving phone' },
  { key: 'receivingAddress', label: 'Receiving address', long: true },
  { key: 'dockHours', label: 'Dock hours' },
  { key: 'storageLocation', label: 'Storage location' },
  { key: 'handlingInstructions', label: 'Handling instructions', long: true },
  { key: 'receivingFees', label: 'Receiving fees' },
  { key: 'roomAssignments', label: 'Room assignments', long: true },
  { key: 'returnFreightPlan', label: 'Return freight', long: true },
  { key: 'contingencyPlan', label: 'If the shipment is late', long: true },
  { key: 'disposalPlan', label: 'Disposal', long: true },
  { key: 'staffArrivalTime', label: 'Staff arrival time' },
  { key: 'vehicleAccessNotes', label: 'Vehicle and dock access', long: true },
  { key: 'parkingInstructions', label: 'Parking', long: true },
];
/** How the client described a delivery when they confirmed it. */
const RECEIPT_CONDITIONS: Readonly<Record<string, string>> = {
  COMPLETE: 'All arrived',
  MISSING: 'Something missing',
  DAMAGED: 'Something damaged',
};

const MATERIALS_COUNT_FIELDS: readonly { readonly key: keyof MaterialsPlan; readonly label: string }[] = [
  { key: 'boxCount', label: 'Boxes' },
  { key: 'palletCount', label: 'Pallets' },
  { key: 'crateCount', label: 'Crates' },
  { key: 'staffVehicleCount', label: 'Staff vehicles' },
];

/** The client's receiving plan and what they confirmed on arrival, under the event's shipments. */
export function ClientShipmentsPanel({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const materials = useQuery({ queryKey: ['workspace-materials', eventId], queryFn: ({ signal }) => readWorkspaceMaterials(eventId, signal) });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['workspace-materials', eventId] });
  const save = useMutation({ mutationFn: (changes: Partial<MaterialsPlan>) => saveWorkspaceMaterials(eventId, changes), onSuccess: refresh, onError: (err) => toast.error(errorMessage(err)) });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  if (materials.isPending) return <LoadingState label="Loading the client's plan…" />;
  if (materials.isError) return <ErrorNotice title="Client plan unavailable" message={errorMessage(materials.error)} />;
  const plan = materials.data.plan;
  const shipments = materials.data.shipments;

  function open(): void {
    const next: Record<string, string> = {};
    for (const field of MATERIALS_TEXT_FIELDS) next[field.key] = String(plan?.[field.key] ?? '');
    for (const field of MATERIALS_COUNT_FIELDS) next[field.key] = plan?.[field.key] == null ? '' : String(plan[field.key]);
    setDraft(next);
    setEditing(true);
  }
  function submit(): void {
    const changes: Record<string, unknown> = {};
    for (const field of MATERIALS_TEXT_FIELDS) {
      const value = (draft[field.key] ?? '').trim();
      if (value !== String(plan?.[field.key] ?? '')) changes[field.key] = value;
    }
    for (const field of MATERIALS_COUNT_FIELDS) {
      const raw = (draft[field.key] ?? '').trim();
      const value = raw === '' ? null : Number.parseInt(raw, 10);
      if (value !== (plan?.[field.key] ?? null) && (value === null || Number.isFinite(value))) changes[field.key] = value ?? undefined;
    }
    if (Object.keys(changes).length > 0) save.mutate(changes as Partial<MaterialsPlan>);
    setEditing(false);
  }

  return (
    <div className="space-y-5">
      <Panel className="p-5">
        <CardHeader
          title="The client's receiving plan"
          description="What the client filled in on their portal: where materials go, who signs for them, and how they come back. Edit it here on their behalf."
          aside={<Button variant="secondary" size="compact" onClick={open} disabled={save.isPending}>{plan ? 'Edit' : 'Start the plan'}</Button>}
        />
        {plan ? (
          <FactList
            columns={3}
            showEmpty
            facts={[
              ...MATERIALS_TEXT_FIELDS.map((field) => ({ label: field.label, value: plan[field.key] as string | null | undefined })),
              ...MATERIALS_COUNT_FIELDS.map((field) => ({ label: field.label, value: plan[field.key] == null ? null : String(plan[field.key]) })),
            ]}
          />
        ) : (
          <p className="text-portal-small text-portal-muted">Nothing recorded yet.</p>
        )}
      </Panel>

      <Panel className="p-5">
        <CardHeader
          title="Confirmed on arrival"
          description="The shipments the client can see on their portal, and what they said when each one landed."
          aside={materials.data.awaitingConfirmation > 0 ? <Pill tone="warning">{materials.data.awaitingConfirmation} awaiting the client</Pill> : undefined}
        />
        {shipments.length === 0 ? (
          <EmptyState icon={<PackageIcon aria-hidden />} title="Nothing shipped yet" message="Shipments marked as visible to the client show here once they are on the way." />
        ) : (
          <ul className="divide-y divide-portal-line">
            {shipments.map((shipment) => (
              <li key={shipment.id} className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
                <div>
                  <span className="block text-portal-small font-bold">{shipment.carrier ?? 'Shipment'}</span>
                  <span className="block font-mono text-portal-micro text-portal-muted">{shipment.trackingNumber ?? 'No tracking number'}</span>
                </div>
                {shipment.receipt ? (
                  <span className="text-portal-small text-portal-muted">
                    Confirmed by {shipment.receipt.confirmedByName ?? 'the client'}
                    {shipment.receipt.confirmedAt ? ` on ${readableDate(shipment.receipt.confirmedAt)}` : ''}
                    {' · '}
                    {RECEIPT_CONDITIONS[shipment.receipt.condition] ?? shipment.receipt.condition}
                  </span>
                ) : (
                  <Pill tone="neutral">Awaiting confirmation</Pill>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Modal
        open={editing}
        title="Receiving and freight plan"
        subtitle="Only what you change is sent; the rest stays as the client wrote it."
        onClose={() => setEditing(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={submit} disabled={save.isPending}>Save</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); submit(); }}>
          <div className="grid gap-4 sm:grid-cols-2">
            {MATERIALS_TEXT_FIELDS.filter((field) => !field.long).map((field) => (
              <Field key={field.key} label={field.label} htmlFor={`ws-materials-${field.key}`}>
                <TextInput id={`ws-materials-${field.key}`} value={draft[field.key] ?? ''} onChange={(event) => setDraft((previous) => ({ ...previous, [field.key]: event.target.value }))} />
              </Field>
            ))}
          </div>
          {MATERIALS_TEXT_FIELDS.filter((field) => field.long).map((field) => (
            <Field key={field.key} label={field.label} htmlFor={`ws-materials-${field.key}`}>
              <TextArea id={`ws-materials-${field.key}`} rows={2} value={draft[field.key] ?? ''} onChange={(event) => setDraft((previous) => ({ ...previous, [field.key]: event.target.value }))} />
            </Field>
          ))}
          <div className="grid gap-4 sm:grid-cols-4">
            {MATERIALS_COUNT_FIELDS.map((field) => (
              <Field key={field.key} label={field.label} htmlFor={`ws-materials-${field.key}`}>
                <TextInput id={`ws-materials-${field.key}`} type="number" min={0} value={draft[field.key] ?? ''} onChange={(event) => setDraft((previous) => ({ ...previous, [field.key]: event.target.value }))} />
              </Field>
            ))}
          </div>
        </form>
      </Modal>
    </div>
  );
}
